import type { CSSProperties, ReactNode } from 'react';
import type { PortalSectionType } from '@breaker/shared-types';
import { deriveBrandVars } from '@/lib/brand-preview';
import './site-preview.css';

/**
 * Tri-surface preview (W4), rendered from DRAFT tokens + draft content —
 * pure client-side, keystroke-instant. Surface 1 is a scoped replica of
 * the portal's public site (drift note in site-preview.css); surfaces 2
 * and 3 are faithful sketches of the tracking card and the driver app's
 * phone frame — brand tokens only, no content slots, as specified.
 */

export interface PreviewSectionData {
  type: PortalSectionType;
  content: Record<string, unknown>;
}

// ---- rich-text walker (same whitelist as the portal's) -------------------

function marksWrap(text: string, marks: Array<{ type: string }> | undefined, key: number): ReactNode {
  let out: ReactNode = text;
  for (const mark of marks ?? []) {
    if (mark.type === 'bold') out = <strong key={key}>{out}</strong>;
    else if (mark.type === 'italic') out = <em key={key}>{out}</em>;
  }
  return typeof out === 'string' ? <span key={key}>{out}</span> : out;
}

function RichTextPreview({ doc }: { doc: unknown }) {
  const d = doc as {
    type?: string;
    content?: Array<{
      type: string;
      content?: Array<Record<string, unknown>>;
    }>;
  };
  if (!d || d.type !== 'doc') return null;
  return (
    <>
      {(d.content ?? []).map((block, i) => {
        if (block.type === 'paragraph') {
          return (
            <p key={i}>
              {(block.content ?? []).map((t, j) =>
                marksWrap(String(t['text'] ?? ''), t['marks'] as never, j),
              )}
            </p>
          );
        }
        const List = block.type === 'orderedList' ? 'ol' : 'ul';
        return (
          <List key={i}>
            {(block.content ?? []).map((item, j) => (
              <li key={j}>
                {((item['content'] as Array<Record<string, unknown>>) ?? []).map((p, k) => (
                  <span key={k}>
                    {((p['content'] as Array<Record<string, unknown>>) ?? []).map((t, l) =>
                      marksWrap(String(t['text'] ?? ''), t['marks'] as never, l),
                    )}
                  </span>
                ))}
              </li>
            ))}
          </List>
        );
      })}
    </>
  );
}

// ---- one preview renderer per section type -------------------------------

function PreviewSection({ section }: { section: PreviewSectionData }) {
  const c = section.content;
  switch (section.type) {
    case 'hero':
      return (
        <section className="sitePrev-hero">
          {c['imageUrl'] ? (
            <div
              className="sitePrev-hero-image"
              style={{ backgroundImage: `url(${String(c['imageUrl'])})` }}
            />
          ) : null}
          <h1>{String(c['headline'] ?? '')}</h1>
          <p className="sitePrev-hero-sub">{String(c['subline'] ?? '')}</p>
          <div className="sitePrev-hero-actions">
            <span className="sitePrev-btn-accent">Book a delivery</span>
            <span className="sitePrev-btn-primary">Sign in</span>
          </div>
        </section>
      );
    case 'about':
      return (
        <section className="sitePrev-about">
          <div>
            <h2>{String(c['heading'] ?? '')}</h2>
            <p>{String(c['body'] ?? '')}</p>
          </div>
          {c['photoUrl'] ? <img src={String(c['photoUrl'])} alt="" /> : null}
        </section>
      );
    case 'highlights':
      return (
        <section className="sitePrev-highlights">
          <ul>
            {((c['items'] as string[]) ?? []).map((label, i) => (
              <li key={i}>{label}</li>
            ))}
          </ul>
        </section>
      );
    case 'gallery': {
      const images = (c['images'] as Array<{ url: string; caption: string | null }>) ?? [];
      if (images.length === 0) return null;
      return (
        <section className="sitePrev-gallery">
          {images.map((img, i) => (
            <figure key={i}>
              <img src={img.url} alt={img.caption ?? ''} />
              {img.caption ? <figcaption>{img.caption}</figcaption> : null}
            </figure>
          ))}
        </section>
      );
    }
    case 'faq': {
      const items = (c['items'] as Array<{ q: string; a: string }>) ?? [];
      if (items.length === 0) return null;
      return (
        <section className="sitePrev-faq">
          <h2>Common questions</h2>
          {items.map((item, i) => (
            <details key={i} open={i === 0}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </section>
      );
    }
    case 'contact': {
      const show = (c['show'] as Record<string, boolean>) ?? {};
      const rows: Array<[string, string]> = [];
      if (show['phone'] && c['phone']) rows.push(['Phone', String(c['phone'])]);
      if (show['email'] && c['email']) rows.push(['Email', String(c['email'])]);
      if (show['address'] && c['address']) rows.push(['Address', String(c['address'])]);
      if (show['openingHours'] && c['openingHours'])
        rows.push(['Hours', String(c['openingHours'])]);
      if (rows.length === 0) return null;
      return (
        <section className="sitePrev-contact">
          <h2>Get in touch</h2>
          <dl>
            {rows.map(([label, value]) => (
              <div key={label} className="row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      );
    }
    case 'coverage': {
      const areas = (c['areas'] as string[]) ?? [];
      if (areas.length === 0) return null;
      return (
        <section className="sitePrev-coverage">
          <h2>Where we deliver</h2>
          <ul>
            {areas.map((area, i) => (
              <li key={i}>{area}</li>
            ))}
          </ul>
        </section>
      );
    }
    case 'text':
      return (
        <section className="sitePrev-text">
          <RichTextPreview doc={c['doc']} />
        </section>
      );
    case 'cta':
      return (
        <section className="sitePrev-cta">
          <p>{String(c['line'] ?? '')}</p>
          <span className="sitePrev-btn-accent">Book a delivery</span>
        </section>
      );
    default:
      return null;
  }
}

// ---- surface 1: the site page --------------------------------------------

export function SitePagePreview({
  fleetName,
  logoUrl,
  primaryHex,
  accentHex,
  sections,
  frame,
  nav = [],
}: {
  fleetName: string;
  logoUrl: string | null;
  primaryHex: string | null;
  accentHex: string | null;
  sections: PreviewSectionData[];
  frame: 'phone' | 'desktop';
  /** W6: nav_visible pages in order (Home excluded), current highlighted. */
  nav?: Array<{ title: string; current?: boolean }>;
}) {
  const vars = deriveBrandVars(primaryHex, accentHex) as CSSProperties;
  return (
    <div
      className="sitePrev mx-auto w-full"
      style={{ ...vars, maxWidth: frame === 'phone' ? 340 : 720 }}
    >
      <div className="sitePrev-scroll">
        <div className="sitePrev-topbar">
          {logoUrl ? <img src={logoUrl} alt="" /> : <strong>{fleetName}</strong>}
          <span className="sitePrev-nav">
            {nav.map((n, i) => (
              <span key={i} className={n.current ? 'is-current' : undefined}>
                {n.title}
              </span>
            ))}
            <span className="sitePrev-signin">Sign in</span>
          </span>
        </div>
        <main>
          {sections.map((s, i) => (
            <PreviewSection key={i} section={s} />
          ))}
        </main>
      </div>
    </div>
  );
}

// ---- surfaces 2 + 3: tracking card + driver phone frame ------------------

export function TrackingCardPreview({
  primaryHex,
  accentHex,
  fleetName,
}: {
  primaryHex: string | null;
  accentHex: string | null;
  fleetName: string;
}) {
  const vars = deriveBrandVars(primaryHex, accentHex);
  const primary = vars['--brand-primary'] ?? '#13293d';
  const accent = vars['--brand-accent'] ?? '#f2a03d';
  const accentInk = vars['--brand-accent-ink'] ?? '#3d2607';
  return (
    <div className="rounded-xl border border-breaker-line bg-white p-4 w-full max-w-[280px]">
      <p className="text-xs font-semibold mb-2" style={{ color: primary }}>
        {fleetName} · live tracking
      </p>
      <div className="relative h-16 rounded-md" style={{ background: `${primary}14` }}>
        <div
          className="absolute left-3 right-3 top-1/2 h-0.5 -translate-y-1/2 rounded"
          style={{ background: primary }}
        />
        <span
          className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full"
          style={{ left: '58%', background: accent, boxShadow: `0 0 0 4px ${accent}44` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-2xs text-breaker-mute">On its way</span>
        <span
          className="rounded-full px-2 py-0.5 text-2xs font-bold"
          style={{ background: accent, color: accentInk }}
        >
          ETA 14:20
        </span>
      </div>
    </div>
  );
}

export function DriverAppPreview({
  primaryHex,
  accentHex,
  fleetName,
  logoUrl,
}: {
  primaryHex: string | null;
  accentHex: string | null;
  fleetName: string;
  logoUrl: string | null;
}) {
  const vars = deriveBrandVars(primaryHex, accentHex);
  const primary = vars['--brand-primary'] ?? '#1a1a1a';
  const accent = vars['--brand-accent'] ?? '#404040';
  const accentInk = vars['--brand-accent-ink'] ?? '#ffffff';
  return (
    <div className="w-[170px] rounded-[22px] border-4 border-breaker-ink bg-[#FAFAF9] p-2 shadow-sm">
      <div className="flex items-center gap-1.5 px-1 pb-2 pt-1">
        <span
          className="flex h-5 w-5 items-center justify-center overflow-hidden rounded text-[9px] font-extrabold text-white"
          style={{ background: primary }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-4 w-4 object-contain" />
          ) : (
            fleetName[0]?.toUpperCase()
          )}
        </span>
        <span className="truncate text-[10px] font-semibold text-[#1A1A1A]">
          {fleetName}
        </span>
      </div>
      <div className="rounded-lg border border-[#E5E5E2] bg-white p-2">
        <span
          className="inline-block rounded-full px-1.5 py-0.5 text-[8px] font-bold"
          style={{ background: accent, color: accentInk }}
        >
          ● En route
        </span>
        <p className="mt-1 text-[10px] font-extrabold text-[#0A0A0A]">
          B12 <span style={{ color: accent }}>»</span> LS9
        </p>
        <div
          className="mt-1.5 rounded-md py-1.5 text-center text-[9px] font-bold"
          style={{ background: accent, color: accentInk }}
        >
          Arrive
        </div>
      </div>
      <div className="mx-auto mt-2 h-1 w-10 rounded bg-[#D4D4D1]" />
    </div>
  );
}
